import { MOCK_BRACKET_MATCHES, type MockMatch, type MockTeam } from '@/features/marketing/mockData/tournament'

/**
 * 8-team single-elim bracket mockup. Compact column-per-round
 * layout — visually mirrors the real `StadiumBracket` so visitors
 * recognise the pattern when they see it inside the product.
 *
 * State shown: 4 QF complete · 1 SF in-progress · 1 SF complete ·
 * GF awaiting SF1 winner. The mid-tournament shot demonstrates
 * winner highlights AND TBD slots in a single frame.
 */
export function BracketMockup() {
  const byRound = groupByRound(MOCK_BRACKET_MATCHES)
  return (
    <div className="flex h-full items-stretch gap-3 p-4">
      {byRound.map((round, idx) => (
        <RoundColumn
          key={idx}
          round={round}
          roundIndex={idx}
          totalRounds={byRound.length}
        />
      ))}
    </div>
  )
}

function RoundColumn({
  round,
  roundIndex,
  totalRounds,
}: {
  round: MockMatch[]
  roundIndex: number
  totalRounds: number
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <p className="font-display mb-2 text-center text-[10px] font-extrabold tracking-[0.22em] text-fg-subtle uppercase">
        {roundLabel(roundIndex, totalRounds)}
      </p>
      <div className="flex flex-1 flex-col justify-evenly gap-1.5">
        {round.map((m) => (
          <MatchCard key={m.matchId} match={m} />
        ))}
      </div>
    </div>
  )
}

function MatchCard({ match }: { match: MockMatch }) {
  const winnerId = match.winnerTeamId
  const isLive = match.status === 'IN_PROGRESS'
  const accent = isLive
    ? 'border-primary'
    : match.kind === 'GF'
      ? 'border-warning/70'
      : 'border-border'
  const shadow = isLive
    ? 'shadow-[0_0_24px_-8px_rgba(61,111,255,0.55)]'
    : ''
  return (
    <div className={`overflow-hidden rounded-lg border bg-bg/60 ${accent} ${shadow}`}>
      <TeamRow team={match.participantA} winner={winnerId === match.participantA?.teamId} loser={!!winnerId && winnerId !== match.participantA?.teamId} live={isLive} />
      <div className="h-px bg-border/60" />
      <TeamRow team={match.participantB} winner={winnerId === match.participantB?.teamId} loser={!!winnerId && winnerId !== match.participantB?.teamId} live={isLive} />
    </div>
  )
}

function TeamRow({
  team,
  winner,
  loser,
  live,
}: {
  team: MockTeam | null
  winner: boolean
  loser: boolean
  live: boolean
}) {
  const name = team?.teamLabel ?? 'Awaiting'
  const accent = winner
    ? 'text-success'
    : loser
      ? 'text-fg-subtle'
      : live
        ? 'text-primary'
        : 'text-fg'
  return (
    <div
      className="flex items-center gap-2 px-2 py-1.5"
      style={{
        opacity: loser ? 0.5 : 1,
        backgroundColor: winner ? 'rgba(0,255,136,0.06)' : 'transparent',
      }}
    >
      {team?.avatarUrl ? (
        <img
          src={team.avatarUrl}
          alt=""
          className="size-5 shrink-0 rounded object-cover"
          loading="lazy"
        />
      ) : (
        <span className="font-display flex size-5 shrink-0 items-center justify-center rounded bg-bg/40 text-[9px] font-black text-fg-subtle">
          ?
        </span>
      )}
      <span className={`font-display flex-1 truncate text-[11px] font-semibold ${accent}`}>
        {name}
      </span>
      {winner && <span className="text-[10px] font-extrabold text-success">✓</span>}
    </div>
  )
}

function groupByRound(matches: MockMatch[]): MockMatch[][] {
  const byRound = new Map<number, MockMatch[]>()
  for (const m of matches) {
    const list = byRound.get(m.round) ?? []
    list.push(m)
    byRound.set(m.round, list)
  }
  return [...byRound.keys()]
    .sort((a, b) => a - b)
    .map((r) => (byRound.get(r) ?? []).sort((a, b) => a.matchIndex - b.matchIndex))
}

function roundLabel(index: number, total: number): string {
  const fromEnd = total - 1 - index
  if (fromEnd === 0) return 'Final'
  if (fromEnd === 1) return 'Semifinal'
  if (fromEnd === 2) return 'Quarterfinal'
  return `Round ${index + 1}`
}
